import { atom, getDefaultStore } from 'jotai';
import { focusAtom } from 'jotai-optics';
import {
    RESET,
    atomWithDefault,
    atomWithReset,
    atomWithStorage,
    loadable,
} from 'jotai/utils';
import snakecaseKeys from 'snakecase-keys';

import { IAssetStateData } from '../interfaces/IAssetState';
import {
    IConfig,
    IErrorReason,
    IHouseType,
    ILeasingType,
} from '../interfaces/IConfig';
import ICredentials from '../interfaces/ICredentials';
import { IExtendedJob, IJob, IJobList } from '../interfaces/IJob';
import IOddoDevicesResponse from '../interfaces/IOdooDeviceResponse';
import IOdooRoomsResponse, {
    IOdooRoom,
    IRoomWithDevices,
    IRoomWithDevicesAndFwInfo,
} from '../interfaces/IOdooRoomsResponse';
import IState from '../interfaces/IState';
import IToken from '../interfaces/IToken';
import { JobType } from '../pages/Jobs';
import {
    THomeAssetData,
    THomeAssetDataWithFWAndCalibration,
} from '../types/HomeAssetData';
import { API_URL, MAX_TEMP, ODOO_INST_STATES } from '../utils/constants';
import fetchWrapper, { customFetch } from '../utils/fetchWrapper';
import { stateData } from './Defaults';
import {
    devicesStateRequest,
    generalTermsRequest,
    interventionStateRequest,
    jobDetailsRequest,
    jobsListRequest,
    photosListRequest,
    trackActionsRequest,
} from './Requests';

const defaultStore = getDefaultStore();

// Authentication Atoms

export const accessTokenAtom = atomWithStorage('accessToken', '', undefined, {
    getOnInit: true,
});
export const refreshTokenAtom = atomWithStorage(
    'refreshToken',
    '',
    undefined,
    {
        getOnInit: true,
    }
);
export const loginAtom = atom(
    null,
    async (get, set, credentials: ICredentials) => {
        try {
            const token: IToken = await fetchWrapper(`${API_URL}/login/`, {
                method: 'POST',
                body: JSON.stringify({
                    username: credentials.username,
                    password: credentials.password,
                }),
            });

            set(accessTokenAtom, token.access);
            set(refreshTokenAtom, token.refresh);
        } catch (e) {
            throw e;
        }
    }
);
export const updateRefreshTokenAtom = atom(null, async (get, set) => {
    const response = await customFetch(`${API_URL}/token/refresh/`, {
        method: 'POST',
        body: JSON.stringify({
            refresh: get(refreshTokenAtom),
        }),
    });

    if (response.status >= 200 && response.status < 300) {
        const token: IToken = await response.json();
        set(accessTokenAtom, token.access);
        set(refreshTokenAtom, token.refresh);
    } else {
        set(accessTokenAtom, RESET);
        set(refreshTokenAtom, RESET);
    }
});

// Jobs Atoms

const jobsListVersionAtom = atom(+new Date());
const jobsListAtom = atomWithDefault<Promise<IJobList[]>>(
    async (get, { signal }) => {
        get(jobsListVersionAtom);
        return jobsListRequest(get(accessTokenAtom), signal);
    }
);
export const jobsListLoadableAtom = atom(
    (get) => get(loadable(jobsListAtom)),
    async (get, set) => {
        const jobs = await jobsListRequest(get(accessTokenAtom));
        set(jobsListAtom, jobs);
    }
);
export const jobsListResetAtom = atom(null, (get, set) => {
    set(jobsListVersionAtom, +new Date());
    set(jobsListAtom, RESET);
});

const jobDetailsVersionAtom = atom(+new Date());
export const jobDetailsAtom = atomWithDefault<Promise<IJob>>(
    async (get, { signal }) => {
        get(jobDetailsVersionAtom);
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        return jobDetailsRequest(accessToken, jobId, signal);
    }
);
export const jobDetailsLoadableAtom = atom(
    (get) => get(loadable(jobDetailsAtom)),
    async (get, set) => {
        const job = await jobDetailsRequest(
            get(accessTokenAtom),
            get(jobIdAtom)
        );
        set(jobDetailsAtom, job);
    }
);
export const jobDetailsResetAtom = atom(null, (get, set) => {
    set(jobDetailsVersionAtom, +new Date());
    set(jobDetailsAtom, RESET);
});

const extendedJobDetailsAtom = atom<Promise<IExtendedJob>>(async (get) => {
    const [jobId, jobsList, jobDetails, photosList] = await Promise.all([
        get(jobIdAtom),
        get(jobsListAtom),
        get(jobDetailsAtom),
        get(photosListAtom),
    ]);

    const extendedJobDetails: IJobList | undefined = jobsList.find(
        (job) => job.id === jobId
    );

    if (!extendedJobDetails) {
        throw new Error('Job not found');
    }

    return {
        ...extendedJobDetails,
        ...jobDetails,
        id: jobId,
        photosList,
    };
});
export const extendedJobDetailsLoadableAtom = atom(
    (get) => get(loadable(extendedJobDetailsAtom)),
    async (_get, set) => {
        await Promise.all([
            set(jobsListLoadableAtom),
            set(jobDetailsLoadableAtom),
            set(photosListLoadableAtom),
        ]);
    }
);

export const organisationalUnitAtom = atom(async (get) => {
    const jobDetails = await get(jobDetailsAtom);
    return jobDetails.organisationalUnit;
});
export const typeInstallationAtom = atom(async (get) => {
    const jobDetails = await get(jobDetailsAtom);
    return jobDetails.typeInstallation;
});
export const deepomaticWorkOrderIdAtom = atom(async (get) => {
    const jobDetails = await get(jobDetailsAtom);
    return jobDetails.imageClassificationWorkOrderId;
});

// Past Jobs Atom
const pastJobsListVersionAtom = atom(+new Date());
const pastJobsListAtom = atomWithDefault<Promise<IJobList[]>>(
    async (get, { signal }) => {
        get(pastJobsListVersionAtom);
        return jobsListRequest(get(accessTokenAtom), signal, JobType.PAST);
    }
);
export const pastJobsListLoadableAtom = atom(
    (get) => get(loadable(pastJobsListAtom)),
    async (get, set) => {
        const jobs = await jobsListRequest(
            get(accessTokenAtom),
            undefined,
            JobType.PAST
        );
        set(pastJobsListAtom, jobs);
    }
);
export const pastJobsListResetAtom = atom(null, (get, set) => {
    set(pastJobsListVersionAtom, +new Date());
    set(pastJobsListAtom, RESET);
});

const extendedPastJobDetailsAtom = atom<Promise<IExtendedJob>>(async (get) => {
    const [jobId, jobsList, jobDetails, photosList] = await Promise.all([
        get(jobIdAtom),
        get(pastJobsListAtom),
        get(jobDetailsAtom),
        get(photosListAtom),
    ]);

    const extendedJobDetails: IJobList | undefined = jobsList.find(
        (job) => job.id === jobId
    );

    if (!extendedJobDetails) {
        throw new Error('Job not found');
    }

    return {
        ...extendedJobDetails,
        ...jobDetails,
        id: jobId,
        photosList,
    };
});
export const extendedPastJobDetailsLoadableAtom = atom(
    (get) => get(loadable(extendedPastJobDetailsAtom)),
    async (_get, set) => {
        await Promise.all([
            set(pastJobsListLoadableAtom),
            set(jobDetailsLoadableAtom),
            set(photosListLoadableAtom),
        ]);
    }
);

// Photos Atoms

const photosListVersionAtom = atom(+new Date());
export const photosListAtom = atomWithDefault<Promise<string[]>>(
    async (get, { signal }) => {
        get(photosListVersionAtom);
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        return photosListRequest(accessToken, jobId, signal);
    }
);
export const photosListLoadableAtom = atom(
    (get) => get(loadable(photosListAtom)),
    async (get, set) => {
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        const photos = await photosListRequest(accessToken, jobId);
        set(photosListAtom, Promise.resolve(photos));
    }
);
export const photosListResetAtom = atom(null, (get, set) => {
    set(photosListVersionAtom, +new Date());
    set(photosListAtom, RESET);
});

// Agreement Atoms

const generalTermsVersionAtom = atom(+new Date());
export const generalTermsAtom = atomWithDefault<Promise<boolean>>(
    async (get, { signal }) => {
        get(generalTermsVersionAtom);
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        return generalTermsRequest(accessToken, jobId, signal);
    }
);
const generalTermsLoadableAtom = atom(
    (get) => get(loadable(generalTermsAtom)),
    async (get, set) => {
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        const agreementExists = await generalTermsRequest(accessToken, jobId);
        set(generalTermsAtom, agreementExists);
    }
);
const gtaAgreementResetAtom = atom(null, (get, set) => {
    set(generalTermsVersionAtom, +new Date());
    set(generalTermsAtom, RESET);
});

// Installation Atoms

export const startInstallationAtom = atom(null, async (get) => {
    try {
        const jobId = get(jobIdAtom);
        const accessToken = get(accessTokenAtom);
        const jobDetails = await get(jobDetailsAtom);

        await fetchWrapper(`${API_URL}/interventions/${jobId}/start/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (jobDetails.state === ODOO_INST_STATES.RUNNING) {
            trackActionsRequest('continue_intervention', {
                jobId,
            });
        } else {
            trackActionsRequest('start_intervention', {
                jobId,
            });
        }
    } catch (e) {
        throw e;
    }
});
export const errorReasonsAtom = atom(async (get, { signal }) => {
    try {
        const errorReasons: IErrorReason[] = await fetchWrapper(
            `${API_URL}/error-reasons/`,
            {
                signal,
            }
        );

        return errorReasons;
    } catch (e) {
        throw e;
    }
});

// Config Atoms

export const configAtom = atom(async (get, { signal }) => {
    try {
        const config: IConfig = await fetchWrapper(`${API_URL}/config/`, {
            signal,
        });
        return config;
    } catch (e) {
        throw e;
    }
});
export const configLoadableAtom = loadable(configAtom);
// House Atoms

export const houseTypesAtom = atom(async (get, { signal }) => {
    try {
        const houseTypes: IHouseType[] = await fetchWrapper(
            `${API_URL}/house-types/`,
            {
                signal,
            }
        );

        return houseTypes;
    } catch (e) {
        throw e;
    }
});
export const leasingTypesAtom = atom(async (get, { signal }) => {
    try {
        const leasingTypes: ILeasingType[] = await fetchWrapper(
            `${API_URL}/leasing-types/`,
            {
                signal,
            }
        );

        return leasingTypes;
    } catch (e) {
        throw e;
    }
});
export const housePropertiesAtom = atom(async (get, { signal }) => {
    try {
        const jobId = get(jobIdAtom);
        const accessToken = get(accessTokenAtom);
        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/house-properties/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
    } catch (e) {
        throw e;
    }
});

// Rooms Atoms
export const defaultRoomsListAtom = atom(async (get, { signal }) => {
    try {
        const jobId = get(jobIdAtom);
        const response: IOdooRoomsResponse = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/rooms/`,
            {
                headers: {
                    Authorization: `Bearer ${get(accessTokenAtom)}`,
                },
                signal,
            }
        );
        return response.rooms;
    } catch (e) {
        throw e;
    }
});
const overwrittenRoomsListAtom = atom<IOdooRoom[] | undefined>(undefined);
export const roomsListAtom = atom(
    (get) => get(overwrittenRoomsListAtom) || get(defaultRoomsListAtom),
    (_get, set, rooms: IOdooRoom[]) => set(overwrittenRoomsListAtom, rooms)
);
const roomsListReadOnlyLoadableAtom = loadable(roomsListAtom);
export const roomsListLoadableAtom = atom(
    (get) => get(roomsListReadOnlyLoadableAtom),
    (get, set, rooms: IOdooRoom[]) => set(roomsListAtom, [...rooms])
);

// Devices Atoms
export const defaultDevicesListAtom = atom(async (get, { signal }) => {
    try {
        const jobId = get(jobIdAtom);
        const response: IOddoDevicesResponse = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/`,
            {
                headers: {
                    Authorization: `Bearer ${get(accessTokenAtom)}`,
                },
                signal,
            }
        );
        const state = defaultStore.get(interventionDefaultStateAtom);
        const deepomaticCopy = [...state.deepomatic];
        // if device in deepomatic array but not in devices, it means that the device
        // has been removed externally of the InstApp (e.g. by deleting room in ISP)
        let stateNeedsUpdate = false;
        deepomaticCopy
            .filter((deviceMac): deviceMac is string => deviceMac !== null)
            .forEach((deviceMac, index) => {
                if (
                    !response.devices
                        .map((deviceData) => deviceData.macAddress)
                        .includes(deviceMac)
                ) {
                    deepomaticCopy[index] = null;
                    stateNeedsUpdate = true;
                }
            });
        if (stateNeedsUpdate) {
            const newState = { ...state, deepomatic: [...deepomaticCopy] };
            defaultStore.set(interventionDefaultStateAtom, { ...newState });
            await defaultStore.set(updateDBStateAtom);
        }
        return response.devices;
    } catch (e) {
        throw e;
    }
});
const overwrittenDevicesListAtom = atom<THomeAssetData[] | undefined>(
    undefined
);
export const devicesListAtom = atom(
    (get) => get(overwrittenDevicesListAtom) || get(defaultDevicesListAtom),
    (_get, set, devices: THomeAssetData[]) =>
        set(overwrittenDevicesListAtom, devices)
);
const devicesListReadOnlyLoadableAtom = loadable(devicesListAtom);
export const devicesListLoadableAtom = atom(
    (get) => get(devicesListReadOnlyLoadableAtom),
    (get, set, devices: THomeAssetData[]) => set(devicesListAtom, [...devices])
);

export const setTargetTemperatureRequest = async (
    accessToken: string,
    jobId: number,
    homeAssetId: string,
    temperature: number
) => {
    try {
        const payload = snakecaseKeys({
            homeAssetId: homeAssetId,
            seconds: 60,
            temperature: temperature,
        });
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/set-target-temperature/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(payload),
            }
        );
        trackActionsRequest(
            temperature === MAX_TEMP ? 'set_max_temp' : 'set_min_temp',
            payload
        );
    } catch (e) {
        throw e;
    }
};
export const enableFastModeRequest = async (
    accessToken: string,
    jobId: number,
    config?: {
        extendedMetering?: boolean;
        temperature?: boolean;
        humidity?: boolean;
        acceleration?: boolean;
        network?: boolean;
    }
) => {
    try {
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/enable-fast-mode/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(
                    snakecaseKeys({
                        seconds: 60,
                        config: config,
                    })
                ),
            }
        );

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 2000);
        });
    } catch (e) {
        throw e;
    }
};

// State Atoms

const interventionStateVersionAtom = atom(+new Date());
const interventionDefaultStateAtom = atomWithReset<IState>(stateData);
const interventionStateAtom = atomWithDefault(async (get, { signal }) => {
    try {
        get(interventionStateVersionAtom);
        const accessToken = get(accessTokenAtom);
        const jobId = get(jobIdAtom);
        const data = await interventionStateRequest(
            accessToken,
            jobId,
            signal
        );
        defaultStore.set(interventionDefaultStateAtom, {
            ...stateData,
            ...data,
        });
        return data;
    } catch (e: any) {
        if (e.status === 404) {
            // returning empty object instead of throwing error for 404 no state case
            // the object will be validated and the default schema object will
            // be used in loadableStateAtom
            // fixing error were state is persisted from past intervention if
            // you open a new intervention that has a 404 error no state
            defaultStore.set(interventionDefaultStateAtom, stateData);
            return stateData;
        }
        throw e;
    }
});
export const interventionStateLoadableAtom = atom(
    (get) => get(loadable(interventionStateAtom)),
    async (get, set) => {
        try {
            const accessToken = get(accessTokenAtom);
            const jobId = get(jobIdAtom);
            const data = await interventionStateRequest(accessToken, jobId);
            set(interventionDefaultStateAtom, {
                ...stateData,
                ...data,
            });
        } catch (e: any) {
            if (e.status === 404) {
                // returning empty object instead of throwing error for 404 no state case
                // the object will be validated and the default schema object will
                // be used in loadableStateAtom
                // fixing error were state is persisted from past intervention if
                // you open a new intervention that has a 404 error no state
                set(interventionDefaultStateAtom, stateData);
            } else {
                throw e;
            }
        }
    }
);

export const interventionStateResetAtom = atom(null, (get, set) => {
    set(interventionStateVersionAtom, +new Date());
    set(interventionDefaultStateAtom, RESET);
    set(interventionStateAtom, RESET);
});
// TODO: Remove this in the future, and move the request to the requests file
export const updateDBStateAtom = atom(null, async (get, set) => {
    try {
        const state = get(interventionDefaultStateAtom);
        const jobId = get(jobIdAtom);
        const accessToken = get(accessTokenAtom);

        await fetchWrapper(`${API_URL}/interventions/${jobId}/states/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            method: 'POST',
            body: JSON.stringify({
                state: state,
            }),
        });
    } catch (e) {
        throw e;
    }
});

export const deepomaticAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('deepomatic')
);

export const canPerformInstallationAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('inspect').prop('canPerformInstallation')
);
export const pdlValidatedAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('inspect').prop('pdlValidated')
);
export const skipLteDboxAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('wire').prop('skipLteDbox')
);

export const testsPassedAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('test').prop('testsPassed')
);

export const skipTestsAtom = focusAtom(interventionDefaultStateAtom, (optic) =>
    optic.prop('test').prop('skipTests')
);
export const skipFwUpdateAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('mesh').prop('skipFwUpdate')
);
export const skipBleValidationAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('mesh').prop('skipBleValidation')
);
export const meshCompletedAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('mesh').prop('meshCompleted')
);
export const calibrationAckAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('signature').prop('calibrationAck')
);
export const businessAckAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('signature').prop('businessAck')
);
export const tabsEnabledAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('tabsEnabled')
);
export const everythingCleanedAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('document').prop('everythingCleaned')
);
export const guideCustomerAtom = focusAtom(
    interventionDefaultStateAtom,
    (optic) => optic.prop('guideCustomer')
);

// Generic Atoms
export const jobIdAtom = atomWithReset(0);
export const prevInterventionPathnameAtom = atomWithStorage(
    'prevInterventionPathname',
    '',
    undefined,
    {
        getOnInit: true,
    }
);
export const onBeforeUnloadTimeAtom = atomWithStorage(
    'onBeforeUnloadTime',
    0,
    undefined,
    {
        getOnInit: true,
    }
);
export const photosUploadedAtoms = atom<{
    [key: string]: {
        uploaded: boolean;
        mandatory: boolean;
    };
}>({});

// Combined Atoms
const defaultRoomsWithDevicesListAtom = atom(async (get) => {
    const [rooms, devices] = await Promise.all([
        get(roomsListAtom),
        get(devicesListAtom),
    ]);

    return rooms.map((room) => {
        const roomDevices = devices.filter(
            (device) => device.roomId === room.roomId
        );

        return {
            ...room,
            devices: roomDevices,
        } as IRoomWithDevices;
    });
});
export const roomsWithDevicesListAtom = loadable(
    defaultRoomsWithDevicesListAtom
);
export const roomsWithDevicesAndStateAtom = loadable(
    atom(async (get) => {
        const [rooms] = await Promise.all([
            get(defaultRoomsWithDevicesListAtom),
            get(interventionStateAtom),
        ]);

        return rooms;
    })
);

export const roomsWithDevicesAndInterventionStateAtom = loadable(
    atom(async (get) => {
        const [rooms, interventionState, jobDetails] = await Promise.all([
            get(defaultRoomsWithDevicesListAtom),
            get(interventionStateAtom),
            get(jobDetailsAtom),
        ]);

        const homeAssets = rooms.flatMap((room) =>
            room.devices.map((device) => device.homeAssetId)
        );

        let homeId: number = 0;
        const relAssetIds: number[] = [];

        homeAssets.forEach((asset) => {
            const [home, relAssetId] = asset.split('/');
            if (!homeId) {
                homeId = +home;
            }
            relAssetIds.push(+relAssetId);
        });

        const deviceStates: { [key: string]: IAssetStateData } =
            await devicesStateRequest(
                get(accessTokenAtom),
                get(jobIdAtom),
                homeId,
                relAssetIds
            );

        const roomsWithDevicesCalibrationState = rooms.map((room) => {
            room.devices = room.devices.map((device) => {
                const deviceState = deviceStates[device.homeAssetId];
                if (deviceState) {
                    return {
                        ...device,
                        calibrationState: deviceState.calibrationState,
                        firmwareVersion: deviceState.firmwareVersion,
                        lastAverageTemperature:
                            deviceState.lastAverageTemperature,
                        lastTargetTemperature:
                            deviceState.lastTargetTemperature,
                    };
                }
                return device as THomeAssetDataWithFWAndCalibration;
            });
            return room as IRoomWithDevicesAndFwInfo;
        });

        return {
            rooms: roomsWithDevicesCalibrationState,
            interventionState,
            jobDetails,
        };
    })
);

export const roomsWithDevicesJobDetailsAndStateAtom = loadable(
    atom(async (get) => {
        const [rooms, jobDetails] = await Promise.all([
            get(defaultRoomsWithDevicesListAtom),
            get(jobDetailsAtom),
            get(interventionStateAtom),
        ]);
        return { rooms, jobDetails };
    })
);
export const photosWithStateAtom = loadable(
    atom(async (get) => {
        const [photosList] = await Promise.all([
            get(photosListAtom),
            get(interventionStateAtom),
        ]);
        return {
            photosList,
        };
    })
);
export const jobDetailsWithPhotosAndStateAtom = loadable(
    atom(async (get) => {
        const [jobDetails, photoList] = await Promise.all([
            get(jobDetailsAtom),
            get(photosListAtom),
            get(interventionStateAtom),
        ]);
        return {
            jobDetails,
            photoList,
        };
    })
);

export const errorReasonsLoadableAtom = loadable(errorReasonsAtom);

export const errorReasonsWithPhotosAndStateAtom = loadable(
    atom(async (get) => {
        const [errorReasons, photosList] = await Promise.all([
            get(errorReasonsAtom),
            get(photosListAtom),
            get(interventionStateAtom),
        ]);
        return {
            errorReasons,
            photosList,
        };
    })
);
export const jobDetailsWithStateAtom = loadable(
    atom(async (get) => {
        const [jobDetails, errorReasons] = await Promise.all([
            get(jobDetailsAtom),
            get(errorReasonsAtom),
            get(interventionStateAtom),
        ]);
        return {
            jobDetails,
            errorReasons,
        };
    })
);
export const jobDetailsWithHousePropertiesPhotosAndStateAtom = loadable(
    atom(async (get) => {
        const [houseProperties, jobDetails, photoList, devices] =
            await Promise.all([
                get(housePropertiesAtom),
                get(jobDetailsAtom),
                get(photosListAtom),
                get(devicesListAtom),
                get(interventionStateAtom),
            ]);
        return {
            jobDetails,
            houseProperties,
            photoList,
            devices,
        };
    })
);
export const jobDetailsWithHouseAndLeasingTypesAndStateAtom = loadable(
    atom(async (get) => {
        const [houseTypes, leasingTypes, jobDetails, photoList] =
            await Promise.all([
                get(houseTypesAtom),
                get(leasingTypesAtom),
                get(jobDetailsAtom),
                get(photosListAtom),
                get(interventionStateAtom),
            ]);
        return {
            houseTypes,
            leasingTypes,
            jobDetails,
            photoList,
        };
    })
);
export const jobDetailsWithHousePropertiesHouseTypesAndStateAtom = loadable(
    atom(async (get) => {
        const [houseTypes, houseProperties, jobDetails] = await Promise.all([
            get(houseTypesAtom),
            get(housePropertiesAtom),
            get(jobDetailsAtom),
            get(interventionStateAtom),
        ]);
        return {
            houseTypes,
            jobDetails,
            houseProperties,
        };
    })
);

// Page atoms

const installationInspectAtom = atom(async (get) => {
    try {
        const [job, gtCguAgreed] = await Promise.all([
            get(jobDetailsAtom),
            get(generalTermsAtom),
            get(interventionStateAtom),
        ]);

        return {
            job,
            gtCguAgreed,
        };
    } catch (e) {
        throw e;
    }
});
export const installationInspectLoadableAtom = atom(
    (get) => get(loadable(installationInspectAtom)),
    async (_get, set) => {
        await Promise.all([
            set(jobDetailsLoadableAtom),
            set(generalTermsLoadableAtom),
            set(photosListLoadableAtom),
            set(interventionStateLoadableAtom),
        ]);
    }
);

const installationWireRoomsAtom = atom(async (get) => {
    const [rooms, config, jobDetails] = await Promise.all([
        get(defaultRoomsWithDevicesListAtom),
        get(configAtom),
        get(jobDetailsAtom),
        get(interventionStateAtom),
    ]);
    return {
        rooms,
        config,
        jobDetails,
    };
});
export const installationWireRoomsLoadableAtom = atom(
    (get) => get(loadable(installationWireRoomsAtom)),
    async (_get, set) => {
        await Promise.all([
            // TODO: Add the missing atoms
            // set(defaultRoomsWithDevicesListAtom),
            // set(configAtom),
            set(jobDetailsLoadableAtom),
            set(interventionStateLoadableAtom),
        ]);
    }
);

const installationWireDevicesAtom = atom(async (get) => {
    const [rooms, config, jobDetails] = await Promise.all([
        get(defaultRoomsWithDevicesListAtom),
        get(configAtom),
        get(jobDetailsAtom),
        get(interventionStateAtom),
    ]);
    return {
        rooms,
        config,
        jobDetails,
    };
});
export const installationWireDevicesLoadableAtom = atom(
    (get) => get(loadable(installationWireDevicesAtom)),
    async (_get, set) => {
        await Promise.all([
            // TODO: Add the missing atoms
            // set(defaultRoomsWithDevicesListAtom),
            // set(configAtom),
            set(interventionStateLoadableAtom),
        ]);
    }
);

// Reset combined atoms

export const resetInterventionAtom = atom(null, (get, set) => {
    set(jobDetailsResetAtom);
    set(photosListResetAtom);
    set(gtaAgreementResetAtom);
    set(interventionStateResetAtom);
    set(overwrittenRoomsListAtom, undefined);
    set(overwrittenDevicesListAtom, undefined);
});

// Atom to reset all the state. Useful to be called on logout
export const resetAllAtom = atom(null, (get, set) => {
    set(pastJobsListResetAtom);
    set(jobsListResetAtom);
    set(jobDetailsResetAtom);
    set(photosListResetAtom);
    set(gtaAgreementResetAtom);
    set(jobIdAtom, RESET);
    set(accessTokenAtom, RESET);
    set(refreshTokenAtom, RESET);
});
